import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import LoginPage from "@/pages/Login";
import LeaderSignup from "@/pages/LeaderSignup";
import RecruitSignup from "@/pages/RecruitSignup";
import Handshake from "@/pages/Handshake";
import RecruitDashboard from "@/pages/RecruitDashboard";
import LeaderDashboard from "@/pages/LeaderDashboard";
import LeadCockpit from "@/pages/LeadCockpit";
import KnowledgeBase from "@/pages/KnowledgeBase";
import AdminPreview from "@/pages/AdminPreview";
import LeaderPreview from "@/pages/LeaderPreview";
import AlfredLocal from "@/pages/AlfredLocal";
import Demo from "@/pages/Demo";
import Arsenal from "@/pages/Arsenal";
import Guide from "@/pages/Guide";
import Bentley from "@/pages/Bentley";
import Brief from "@/pages/Brief";
import FoyerPreview from "@/pages/FoyerPreview";
import MotoFoyer from "@/pages/MotoFoyer";
import Training from "@/pages/Training";
import Uprising from "@/pages/Uprising";
import SimsCommand from "@/pages/SimsCommand";
import GreenShield from "@/pages/GreenShield";
import MobileCycle from "@/pages/MobileCycle";
import Blueprint from "@/pages/Blueprint";
// ---------------------------------------------------------------
// HALT · Isolated verticals — DO NOT re-enable without operator OK.
// Cierra strike is the only new addition going to production.
// Re-enable by uncommenting the imports below AND the Route
// declarations further down in AppRoutes.
// ---------------------------------------------------------------
// import Mixology from "@/pages/Mixology";
import MagicGarage from "@/pages/MagicGarage";
// import MSquared from "@/pages/MSquared";
import Cierra from "@/pages/Cierra";
import Founders from "@/pages/Founders";
import Affordable from "@/pages/Affordable";
import Induction from "@/pages/Induction";
import JDEnterprises from "@/pages/JDEnterprises";
import LeaderBriefings from "@/pages/LeaderBriefings";
import ActivateNode from "@/pages/ActivateNode";
import ActivateNodeSuccess from "@/pages/ActivateNodeSuccess";
import Roundtable from "@/pages/Roundtable";
import Register from "@/pages/Register";
import Academy from "@/pages/Academy";
import AmandaAvatar from "@/components/AmandaAvatar";
import GridConcierge from "@/components/GridConcierge";
import SeoHelmet from "@/components/SeoHelmet";
import { ComplianceFooter } from "@/components/ComplianceFooter";
import { trackEvent, initTracking } from "@/utils/lawnStrikeTracker";
import "@/App.css";

const ProtectedRoute = ({ children, requireRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060B14]">
        <div className="hud-label">Initializing flight systems...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireRole && user.role !== requireRole) {
    return <Navigate to={user.role === "leader" ? "/leader" : "/dashboard"} replace />;
  }
  // Recruit gatekeeper: redirect to /handshake unless already there
  if (user.role === "recruit" && !user.handshake_signed && location.pathname !== "/handshake") {
    return <Navigate to="/handshake" replace />;
  }
  return children;
};

function RouteSeo() {
  const { pathname } = useLocation();
  // Normalize trailing slash so "/" and "/founders/" both resolve cleanly.
  const key = pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  return <SeoHelmet routeKey={key} />;
}

// ---------------------------------------------------------------
// Vertical routes render an isolated "app within an app" — the
// global ComplianceFooter is intentionally hidden on these paths
// so users see only their vertical cockpit chrome.
// ---------------------------------------------------------------
const VERTICAL_ROUTES = [
  "/green-shield",
  "/uprising",
  "/sims",
  "/moto",
  "/mobile-cycle",
  "/mixology",
  "/magic-garage",
  "/msquared",
  "/blueprint",
  "/cierra",
  "/founders",
  "/affordable",
  "/induction",
  "/jd-enterprises",
  "/lawn-strike",
  "/activate-node",
  "/roundtable",
  "/register",
  "/academy",
];

function ConditionalFooter() {
  const { pathname } = useLocation();
  const hideFooter = VERTICAL_ROUTES.some((r) => pathname.startsWith(r));
  if (hideFooter) return null;
  return <ComplianceFooter />;
}

// Utility: pause + reset an <Audio> element stored in a useRef so
// the next play() starts cleanly. Safe to call even if the ref is
// empty or the element is mid-teardown.
function stopAudio(ref) {
  const el = ref && ref.current;
  if (!el) return;
  try {
    el.pause();
    el.currentTime = 0;
  } catch (_e) {
    /* audio element already gone — no-op */
  }
  ref.current = null;
}

// ---------------------------------------------------------------
// /lawn-strike — inline dynamic reskin + Virtual Tour with Amanda
// voice-over. Reads `company`, `primary_contact` (or `contact`),
// and `city` from the URL so a single link personalizes per
// prospect. 5-step tour cycles through Greeting → 4 flagship
// modules; each step triggers an Amanda voice clip. No background
// ambient — Amanda is the only voice the prospect hears.
// ---------------------------------------------------------------
const LS_MODULES = [
  {
    id: "greeting",
    label: "Amanda's Greeting",
    tag: "Module 00",
    blurb: "The Grid opens with a personal note — Amanda 4.0 (Black Suit) locks eyes on your Priority Node.",
  },
  {
    id: "intake-shield",
    label: "Intake Shield",
    tag: "Module 01",
    blurb: "Amanda 4.0 answers every ring — mowing, treatment, cleanup — no lost weekend leads.",
  },
  {
    id: "command-calendar",
    label: "Command Calendar",
    tag: "Module 02",
    blurb: "4-lane dispatch board — Mow, Treat, Cleanup, Install — keeps every crew in its own high-velocity route.",
  },
  {
    id: "billing-autopilot",
    label: "Billing Autopilot",
    tag: "Module 03",
    blurb: "Route-close invoicing + automated follow-ups. Amanda chases late pays so you focus on the next lot.",
  },
  {
    id: "growth-engine",
    label: "Growth Engine",
    tag: "Module 04",
    blurb: "10 cinematic before/after videos every week — the neighborhood watches your crew win.",
  },
];

// Amanda voice clips indexed by tourStep. Step 0 fires from the
// START TOUR click handler (unlocks the audio context under
// browser autoplay policy); steps 1-4 fire from the useEffect
// that watches tourStep.
const LS_VOICE_CLIPS = [
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/e9f9204a6fcd44288297480ddc893357_clip1_greeting.mp3",
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/dcd93c90cb1c4150b538fd6488d87f42_clip2_intake.mp3",
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/580cda07c4c346389f5e04779663f0af_clip3_calendar.mp3",
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/6be6b5a851db40d4b3a735dce2c5d498_clip4_billing.mp3",
  // Step 4 · Growth Engine — repurposed as the Interactive Q&A
  // Closer. Amanda invites the prospect to ask anything or lock in.
  "/audio/lawn-strike/clip4_qa_prompt.mp3",
];

// Amanda's static answer clip fired when the prospect submits a
// question on Step 4. Same shimmer voice, tts-1-hd, hosted locally
// under /public so it streams instantly with no backend round-trip.
const LS_QA_ANSWER_CLIP = "/audio/lawn-strike/clip4_qa_answer.mp3";
const LS_QA_ANSWER_TEXT =
  "Great question. We handle all those specifics during your Month 1 Release Phase — we plug into your existing workflow so nothing breaks. The Grid adapts to you. Let's get your primary node locked in.";

function LawnStrikeRoute() {
  const [params] = useSearchParams();
  // Sub-path slug support · `/lawn-strike/:companySlug` lets us
  // hand a prospect a clean shareable URL like
  // /lawn-strike/emi-landscapepros. The slug is only used if no
  // explicit `?company=` query param is present.
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [tourStep, setTourStep] = useState(null);

  // Interactive Q&A Closer (Step 4 · Growth Engine) — the prospect
  // types a question, we reveal Amanda's static answer and auto-
  // play Clip B. qaSubmitted flips once and stays flipped until the
  // user navigates away from Step 4 (or restarts the tour).
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaSubmitted, setQaSubmitted] = useState(false);

  // Amanda voice-over ref — swaps clip on each tourStep change.
  const voiceRef = useRef(null);

  // ---- Priority Node Selection tracking layer -----------------
  // Init on mount (session + PAGE_VIEW + time milestones + exit).
  useEffect(() => {
    const cleanup = initTracking();
    return () => {
      cleanup && cleanup();
      // Belt-and-braces: stop any audio if the operator navigates
      // away mid-tour so a lingering clip can't outlive the page.
      stopAudio(voiceRef);
    };
  }, []);

  // Fire per-step telemetry + reset Q&A state whenever the tour
  // advances. IMPORTANT: audio playback is NOT triggered here.
  // Modern browser autoplay policies (Chrome/Safari/Firefox) block
  // `audio.play()` unless the call sits inside the same call stack
  // as a real user gesture (click/tap). A useEffect firing off a
  // state change does NOT count, so the promise is silently
  // rejected. All audio playback is done directly inside the
  // click handlers below (startTour, advance, back, submitQa).
  useEffect(() => {
    if (tourStep === null) return;
    setQaQuestion("");
    setQaSubmitted(false);
    const stepNames = LS_MODULES.map((m) => m.label);
    trackEvent("TOUR_STEP_VIEW", {
      step: tourStep,
      step_name: stepNames[tourStep],
    });
  }, [tourStep]);

  // Fire-and-forget audio player, safe to call from any click
  // handler. Stops whatever is currently playing on `voiceRef`
  // and starts the new clip. Wrapped in a helper so all four
  // click handlers share exactly the same behaviour.
  const playClip = (url, label) => {
    if (!url) return;
    if (voiceRef.current) {
      try {
        voiceRef.current.pause();
        voiceRef.current.currentTime = 0;
      } catch (_) { /* ignore */ }
    }
    console.log("[LawnStrike] Playing clip:", label, url);
    const a = new Audio(url);
    a.volume = 1.0;
    voiceRef.current = a;
    a.play()
      .then(() => console.log("[LawnStrike] Audio playing successfully:", label))
      .catch((e) => console.warn("[LawnStrike] Audio blocked:", label, e));
  };

  // Company name resolution:
  //   1) `?company=` query param wins (existing behaviour).
  //   2) fallback to the `:companySlug` path segment, de-slugged.
  //   3) final default = "Landscaping Professional".
  const deslug = (s) =>
    s
      .split("-")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  const queryCompany = params.get("company");
  const slugCompany = companySlug ? deslug(companySlug) : "";
  const resolvedCompany = queryCompany || slugCompany || "";
  const company = resolvedCompany || "Landscaping Professional";
  const contact =
    params.get("primary_contact") || params.get("contact") || "friend";
  const city = params.get("city") || "local";
  const headline = resolvedCompany
    ? `Priority Node Selection: ${resolvedCompany}`
    : "Priority Node Selection: Landscaping Pros";
  const greeting = resolvedCompany
    ? `${contact}, we've been mapping the ${city} landscaping market. Based on our audit of local lead-flow, we've reserved this Primary Node specifically for ${company}.`
    : `${contact}, we've been mapping the ${city} landscaping market. Based on our audit of local lead-flow, we've reserved this Primary Node specifically for your operation.`;

  const GOLD = "#C9A84C";
  const GREEN = "#22C55E";
  const BLACK = "#0A0F0C";
  const CARD = "#111814";
  const HAIRLINE = "#1A2620";
  const MUTED = "#9AA79C";

  const highlightStyle = (active) =>
    active
      ? {
          border: `2px solid ${GOLD}`,
          boxShadow: `0 0 0 4px ${GOLD}22, 0 0 44px 8px ${GOLD}88, inset 0 0 24px ${GOLD}44`,
          transform: "translateY(-2px)",
        }
      : { border: `1px solid ${HAIRLINE}`, boxShadow: "none", transform: "none" };

  const startTour = () => {
    // Log the start.
    trackEvent("TOUR_START", {});
    // START TOUR is a direct user gesture → audio context unlocks.
    // Playing Amanda's greeting clip directly in this handler is
    // what makes the rest of the tour audible on Safari/iOS too.
    playClip(LS_VOICE_CLIPS[0], "step-0-greeting");
    setTourStep(0);
  };

  const closeTour = () => {
    if (voiceRef.current) {
      try { voiceRef.current.pause(); } catch (_) { /* ignore */ }
      voiceRef.current = null;
    }
    setTourStep(null);
  };

  const advance = () => {
    if (tourStep === null) return;
    if (tourStep < LS_MODULES.length - 1) {
      const next = tourStep + 1;
      // Play the next clip inline with this click gesture BEFORE
      // setState so we stay inside the same call stack the browser
      // is auditing for autoplay policy.
      playClip(LS_VOICE_CLIPS[next], `step-${next}`);
      setTourStep(next);
    } else {
      // Finishing the tour — fire the high-value event exactly once.
      trackEvent("TOUR_COMPLETE", { total_steps_viewed: LS_MODULES.length });
      closeTour();
    }
  };
  const back = () => {
    if (tourStep && tourStep > 0) {
      const prev = tourStep - 1;
      playClip(LS_VOICE_CLIPS[prev], `step-${prev}`);
      setTourStep(prev);
    }
  };

  // Q&A Closer submit handler — logs the prospect's question, reveals
  // Amanda's static answer, and swaps the audio to Clip B directly
  // from inside this click gesture (so browser autoplay policies
  // allow the play() promise to resolve).
  const submitQaQuestion = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const q = qaQuestion.trim();
    if (!q || qaSubmitted) return;
    trackEvent("QA_QUESTION_ASKED", {
      question: q.slice(0, 500),
      step: 4,
      step_name: "Growth Engine",
    });
    setQaSubmitted(true);
    playClip(LS_QA_ANSWER_CLIP, "qa-answer");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: BLACK,
        color: "#fff",
        padding: "40px 20px 120px",
        fontFamily: "Arial, sans-serif",
      }}
      data-testid="lawn-strike-root"
    >
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* Kicker */}
        <div
          style={{
            display: "inline-block",
            padding: "6px 12px",
            background: GOLD,
            color: "#000",
            fontSize: 11,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            marginBottom: 20,
            borderRadius: 3,
          }}
          data-testid="lawn-strike-kicker"
        >
          Lawn Strike · Priority Node
        </div>

        {/* Hero */}
        <h1
          style={{ color: GOLD, fontSize: "2.6em", lineHeight: 1.1, margin: 0 }}
          data-testid="lawn-strike-hero-title"
        >
          {headline}
        </h1>
        <p
          style={{ fontSize: "1.2em", marginTop: 20, color: "#E8EAE9", maxWidth: 720 }}
          data-testid="lawn-strike-hero-greeting"
        >
          {greeting}
        </p>
        <div style={{ marginTop: 12, color: MUTED, fontSize: "0.95em" }}>
          <span style={{ color: GREEN, fontWeight: 600 }}>{company}</span>
          {" · Berkeley County · Amanda 4.0 (Black Suit) on the line"}
        </div>

        {/* START TOUR button */}
        <button
          type="button"
          data-testid="tour-btn"
          onClick={startTour}
          style={{
            background: GOLD,
            color: "#000",
            padding: "16px 32px",
            fontSize: "1.1em",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            marginTop: 28,
            fontWeight: 700,
            letterSpacing: "0.14em",
            boxShadow: `0 12px 32px -8px ${GOLD}`,
          }}
        >
          START TOUR
        </button>

        {/* Modules with tour highlight — Greeting (index 0) is the
            hero above, so we render only the flagship 4 cards here.
            Highlight uses the ORIGINAL LS_MODULES index so tourStep
            still maps correctly through the tour. */}
        <div
          style={{
            marginTop: 48,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 16,
          }}
          data-testid="lawn-strike-modules"
        >
          {LS_MODULES.map((m, i) => {
            if (m.id === "greeting") return null;
            const active = tourStep === i;
            return (
              <div
                key={m.id}
                id={m.id}
                data-testid={`lawn-strike-module-${m.id}`}
                className={active ? "tour-active" : ""}
                style={{
                  background: CARD,
                  borderRadius: 10,
                  padding: 20,
                  transition: "all 260ms ease",
                  ...highlightStyle(active),
                }}
              >
                <div
                  style={{
                    color: active ? GOLD : MUTED,
                    fontSize: 10,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {m.tag}
                </div>
                <div
                  style={{
                    color: "#fff",
                    fontSize: "1.15em",
                    fontWeight: 600,
                    marginBottom: 8,
                  }}
                >
                  {m.label}
                </div>
                <div style={{ color: MUTED, fontSize: "0.92em", lineHeight: 1.5 }}>
                  {m.blurb}
                </div>
              </div>
            );
          })}
        </div>

        {/* Interactive Q&A Closer · appears only on Step 4 (Growth
            Engine). Text input + gold "Ask Amanda" button; on submit
            we reveal Amanda's static answer and auto-play Clip B. */}
        {tourStep === 4 && (
          <div
            data-testid="lawn-strike-qa-closer"
            style={{
              marginTop: 40,
              background: CARD,
              border: `1px solid ${GOLD}`,
              borderRadius: 12,
              padding: "28px 28px",
              boxShadow: `0 24px 60px -24px ${GOLD}66, inset 0 1px 0 ${GOLD}33`,
            }}
          >
            <div
              style={{
                color: GOLD,
                fontSize: 11,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              Interactive Q&amp;A Closer · Amanda 4.0
            </div>
            <h3
              style={{
                color: "#fff",
                fontSize: "1.5em",
                margin: "0 0 8px",
                lineHeight: 1.2,
              }}
            >
              Ask Amanda anything before you lock in.
            </h3>
            <p style={{ color: MUTED, marginTop: 0, marginBottom: 18 }}>
              Crew access, billing peaks, workflow specifics — whatever&apos;s on
              your mind. Or hit the pricing block above to lock in your Command
              Center.
            </p>
            <form
              onSubmit={submitQaQuestion}
              style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
            >
              <input
                type="text"
                value={qaQuestion}
                onChange={(e) => setQaQuestion(e.target.value)}
                placeholder="Ask Amanda anything…"
                data-testid="lawn-strike-qa-input"
                disabled={qaSubmitted}
                style={{
                  flex: "1 1 320px",
                  background: BLACK,
                  color: "#fff",
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontSize: "1em",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
              <button
                type="submit"
                data-testid="lawn-strike-qa-submit"
                disabled={qaSubmitted || !qaQuestion.trim()}
                style={{
                  background: GOLD,
                  color: "#000",
                  padding: "14px 24px",
                  fontSize: "0.95em",
                  border: "none",
                  borderRadius: 8,
                  cursor:
                    qaSubmitted || !qaQuestion.trim()
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  opacity: qaSubmitted || !qaQuestion.trim() ? 0.55 : 1,
                  boxShadow: `0 12px 30px -12px ${GOLD}`,
                }}
              >
                Ask Amanda
              </button>
            </form>

            {qaSubmitted && (
              <div
                data-testid="lawn-strike-qa-answer"
                style={{
                  marginTop: 22,
                  background: `linear-gradient(160deg, ${GOLD}18 0%, ${BLACK} 80%)`,
                  border: `1px solid ${GOLD}66`,
                  borderRadius: 10,
                  padding: "20px 22px",
                }}
              >
                <div
                  style={{
                    color: GOLD,
                    fontSize: 10,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  Amanda 4.0 · Live Response
                </div>
                <div
                  style={{
                    color: "#fff",
                    fontSize: "1.05em",
                    lineHeight: 1.55,
                  }}
                >
                  {LS_QA_ANSWER_TEXT}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pricing card */}
        <div
          data-testid="lawn-strike-pricing"
          style={{
            marginTop: 56,
            background: `linear-gradient(160deg, ${GOLD}22 0%, ${CARD} 55%, ${BLACK} 100%)`,
            border: `1px solid ${GOLD}`,
            borderRadius: 12,
            padding: "36px 32px",
            boxShadow: `0 40px 90px -30px ${GOLD}55, inset 0 1px 0 ${GOLD}44`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            }}
          />
          <h3
            style={{
              color: GOLD,
              fontSize: 12,
              letterSpacing: "0.34em",
              textTransform: "uppercase",
              margin: 0,
              fontWeight: 700,
            }}
            data-testid="lawn-strike-pricing-title"
          >
            Priority Node Selection Rate
          </h3>
          <div
            style={{
              color: "#fff",
              fontSize: "3.4em",
              fontWeight: 700,
              lineHeight: 1,
              marginTop: 8,
            }}
            data-testid="lawn-strike-pricing-amount"
          >
            $297<span style={{ fontSize: "0.4em", color: MUTED, fontWeight: 400 }}>/mo</span>
          </div>
          <div style={{ color: "#fff", marginTop: 10, fontSize: "1.05em" }}>
            Founder rate locked · Year 1 · Only 12 nodes in this class.
          </div>
          <button
            type="button"
            data-testid="lawn-strike-pricing-cta"
            onClick={() => {
              trackEvent("CONVERSION_INTENT", {
                cta: "LOCK_IN_COMMAND_CENTER",
                price: "$297/mo",
                destination: "/activate-node",
              });
              navigate("/activate-node");
            }}
            style={{
              marginTop: 24,
              background: GOLD,
              color: "#000",
              padding: "16px 28px",
              fontSize: "1em",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              boxShadow: `0 14px 34px -10px ${GOLD}`,
            }}
          >
            Lock In Your Command Center
          </button>
        </div>
      </div>

      {/* Tour Controls overlay */}
      {tourStep !== null && (
        <div
          className="tour-overlay"
          data-testid="lawn-strike-tour-overlay"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: CARD,
            border: `1px solid ${GOLD}`,
            borderRadius: 12,
            padding: "14px 18px",
            boxShadow: `0 30px 60px -20px rgba(0,0,0,0.9), 0 0 32px -8px ${GOLD}88`,
            display: "flex",
            alignItems: "center",
            gap: 14,
            zIndex: 100,
            maxWidth: "calc(100% - 32px)",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              color: GOLD,
              fontSize: 10,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
            data-testid="lawn-strike-tour-step-indicator"
          >
            Step {tourStep + 1} / {LS_MODULES.length}
          </div>
          <div
            style={{ color: "#fff", fontWeight: 600 }}
            data-testid="lawn-strike-tour-current-title"
          >
            {LS_MODULES[tourStep].label}
          </div>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <button
              type="button"
              onClick={back}
              disabled={tourStep === 0}
              data-testid="lawn-strike-tour-prev"
              style={{
                background: "transparent",
                color: tourStep === 0 ? `${MUTED}66` : MUTED,
                border: `1px solid ${HAIRLINE}`,
                padding: "8px 14px",
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                fontWeight: 700,
                borderRadius: 6,
                cursor: tourStep === 0 ? "not-allowed" : "pointer",
              }}
            >
              Prev
            </button>
            <button
              type="button"
              onClick={advance}
              data-testid="lawn-strike-tour-next"
              style={{
                background: GOLD,
                color: "#000",
                border: "none",
                padding: "8px 16px",
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                fontWeight: 700,
                borderRadius: 6,
                cursor: "pointer",
                boxShadow: `0 8px 22px -8px ${GOLD}`,
              }}
            >
              {tourStep < LS_MODULES.length - 1 ? "Next Module" : "Finish Tour"}
            </button>
            <button
              type="button"
              onClick={closeTour}
              data-testid="lawn-strike-tour-close"
              aria-label="Close tour"
              style={{
                background: "transparent",
                color: MUTED,
                border: `1px solid ${HAIRLINE}`,
                padding: "8px 12px",
                fontSize: 12,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AppRoutes() {
  return (
    <>
      <RouteSeo />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/leader-signup" element={<LeaderSignup />} />
      <Route path="/signup-recruit" element={<RecruitSignup />} />
      <Route path="/admin-preview" element={<AdminPreview />} />
      <Route path="/leader-preview" element={<LeaderPreview />} />
      <Route path="/local" element={<AlfredLocal />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/arsenal" element={<Arsenal />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/bentley" element={<Bentley />} />
      <Route path="/brief" element={<Brief />} />
      <Route path="/foyer-preview" element={<FoyerPreview />} />
      <Route path="/moto" element={<MotoFoyer />} />
      <Route path="/training" element={<Training />} />
      <Route path="/uprising" element={<Uprising />} />
      <Route path="/sims" element={<SimsCommand />} />
      <Route path="/green-shield" element={<GreenShield />} />
      <Route path="/mobile-cycle" element={<MobileCycle />} />
      <Route path="/blueprint" element={<Blueprint />} />
      <Route path="/magic-garage" element={<MagicGarage />} />
      {/* HALT · Isolated · Do NOT uncomment without operator OK.
      <Route path="/mixology" element={<Mixology />} />
      <Route path="/msquared" element={<MSquared />} />
      */}
      <Route path="/cierra" element={<Cierra />} />
      <Route path="/founders" element={<Founders />} />
      <Route path="/affordable" element={<Affordable />} />
      <Route path="/induction" element={<Induction />} />
      <Route path="/jd-enterprises" element={<JDEnterprises />} />
      <Route path="/lawn-strike" element={<LawnStrikeRoute />} />
      <Route path="/lawn-strike/:companySlug" element={<LawnStrikeRoute />} />
      <Route path="/activate-node" element={<ActivateNode />} />
      <Route path="/activate-node/success" element={<ActivateNodeSuccess />} />
      <Route path="/roundtable" element={<Roundtable />} />
      <Route path="/register" element={<Register />} />
      <Route path="/academy" element={<Academy />} />
      <Route path="/briefings" element={<LeaderBriefings />} />
      <Route path="/handshake" element={
        <ProtectedRoute requireRole="recruit">
          <Handshake />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute requireRole="recruit">
          <RecruitDashboard />
        </ProtectedRoute>
      } />
      <Route path="/leader" element={
        <ProtectedRoute requireRole="leader">
          <LeaderDashboard />
        </ProtectedRoute>
      } />
      <Route path="/leader/lead/:id" element={
        <ProtectedRoute requireRole="leader">
          <LeadCockpit />
        </ProtectedRoute>
      } />
      <Route path="/knowledge" element={
        <ProtectedRoute>
          <KnowledgeBase />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[#060B14] flex flex-col">
        <BrowserRouter>
          <AuthProvider>
            <div className="flex-1 flex flex-col">
              <AppRoutes />
            </div>
            <ConditionalFooter />
            <GridConcierge />
            <AmandaAvatar />
            <Toaster position="top-right" theme="dark" />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
